import React from 'react';
import useAuth from './hooks/useAuth';
import {useRoutes} from 'react-router-dom';
import routes from './routes';
import {ExtendedRouteObject} from './types/typeRoutes';
import AccessRightsNoPermissionView from './pages/access-rights/AccessRightsNoPermissionView';
import {Loader} from './components';
import {Box} from '@mui/material';

function AppRbacRoutes() {
  const {checkRbac, isInitialized} = useAuth();

  /**
   * Processes routes to apply RBAC (Role-Based Access Control) permissions
   *
   * @param routeData - Array of route objects with optional RBAC properties
   * @returns Modified routes with permission checks applied
   */
  const filterRbacRoutes = (routeData: ExtendedRouteObject[]) => {
    const modifiedRoutes = routeData.map(route => {
      const newRoute = {...route};

      // Process children routes if they exist
      if (newRoute?.children) {
        // Map through children and apply permission checks
        newRoute.children = newRoute.children.map(routeChild => {
          const {element, requiredPermissions, noPermissionElement} =
            routeChild;

          // Only process routes with permission requirements
          if (requiredPermissions) {
            const modifiedChild = {
              ...routeChild,
              // If user has required permissions, show original element
              // Otherwise, show custom fallback or default no-permission view
              element: checkRbac({
                permissionString: requiredPermissions,
              })
                ? element
                : noPermissionElement ?? <AccessRightsNoPermissionView />,
            };

            return modifiedChild;
          }

          // Return unmodified child if no permission requirements
          return routeChild;
        });
      }

      // Return the processed route
      return newRoute;
    });

    return modifiedRoutes;
  };

  const element = useRoutes(filterRbacRoutes(routes));

  if (!isInitialized) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <Loader />
      </Box>
    );
  }

  return element;
}

export default AppRbacRoutes;
